import type { GatingCookiePermissions } from "../../types/definitions/gating/gating-cookie-permissions.js";
import type { GatingState } from "../../types/definitions/gating/gating-state.js";
import { type AnyMarketoInputName } from "../../types/definitions/inputs/marketo-input-names-and-values.js";
import { EnabledFields } from "./enabled-fields.js";

interface MarketoFormSettingsInstance<AllowedGatingState extends GatingState.MarketoForm = GatingState.MarketoForm> {
  marketoFormSettingsKey: string;
  formId: number;
  hasRecaptcha: boolean;
  gatingCookiePermissions: GatingCookiePermissions;
  marketoAllowedGatingStates: readonly AllowedGatingState[];
  enabledFields: {
    [Key in AllowedGatingState]?: readonly AnyMarketoInputName[]
  }
}

export const MarketoFormSettingsObject = {
  contact: {
    marketoFormSettingsKey: "contact",
    formId: 1027,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.Contact.Full
    }
  },
  resource: {
    marketoFormSettingsKey: "resource",
    formId: 1001,
    gatingCookiePermissions: {
      allowRead: true,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["BlindSubmit", "PartialGate", "FullGate"],
    enabledFields: {
      BlindSubmit: EnabledFields.Resource.Hidden,
      PartialGate: EnabledFields.Resource.Partial,
      FullGate: EnabledFields.Resource.Full
    }
  },

  "custom-resource": {
    marketoFormSettingsKey: "custom-resource",
    formId: 1001,
    gatingCookiePermissions: {
      allowRead: true,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["BlindSubmit", "PartialGate", "FullGate"],
    enabledFields: {
      BlindSubmit: EnabledFields.Resource.Hidden,
      PartialGate: EnabledFields.Resource.Partial,
      FullGate: EnabledFields.Resource.Full
    }
  },
  roi: {
    marketoFormSettingsKey: "roi",
    formId: 1016,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.Roi.Full
    }
  },
  spark: {
    marketoFormSettingsKey: "spark",
    formId: 1026,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.Contact.Full
    }
  },
  pricing: {
    marketoFormSettingsKey: "pricing",
    formId: 1028,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.Pricing.Full
    }
  },
  subscription: {
    marketoFormSettingsKey: "subscription",
    formId: 1029,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: false,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.Subscription.Full
    }
  },
  partner: {
    marketoFormSettingsKey: "partner",
    formId: 1024,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.Partner.Full
    }
  },
  "case-study": {
    marketoFormSettingsKey: "case-study",
    formId: 1012,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["PartialGate", "BlindSubmit"],
    enabledFields: {
      BlindSubmit: EnabledFields.Resource.Hidden,
      PartialGate: EnabledFields.Resource.Partial
    }
  },
  "demo-request": {
    marketoFormSettingsKey: "demo-request",
    formId: 1014,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.DemoRequest.Full
    }
  },
  "tradeshow-giveaway": {
    marketoFormSettingsKey: "tradeshow-giveaway",
    formId: 1006,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.TradeShow.Full
    }
  },
  "event-registration": {
    marketoFormSettingsKey: "event-registration",
    formId: 1159,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields: {
      FullGate: EnabledFields.EventRegistration.Full
    }
  },
  "subprocessor-update-notification": {
    marketoFormSettingsKey: "subprocessor-update-notification",
    formId: 1211,
    gatingCookiePermissions: {
      allowRead: false,
      allowWrite: false,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate"],
    enabledFields:  {
      FullGate: EnabledFields.Subprocessor.Full
    }
  },
  "maturity-assessment": {
    marketoFormSettingsKey: "maturity-assessment", //resource
    formId: 1001, //resourceFormID
    gatingCookiePermissions: {
      allowRead: true,
      allowWrite: true,
    } as GatingCookiePermissions,
    hasRecaptcha: true,
    marketoAllowedGatingStates: ["FullGate", "BlindSubmit"],
    enabledFields: {
      FullGate: EnabledFields.Resource.Full
    }
  }
} as const satisfies { [marketoFormSettingsKey: string]: MarketoFormSettingsInstance; };

export type MarketoFormSettingsObject = typeof MarketoFormSettingsObject;

const _marketoFormSettingsArray = Object.values(MarketoFormSettingsObject);

export type MarketoFormSettingsKey = (typeof _marketoFormSettingsArray)[number]["marketoFormSettingsKey"];
export const MarketoFormSettingsKey = Object.freeze(_marketoFormSettingsArray.map(f => f.marketoFormSettingsKey));

export type MarketoFormId = (typeof _marketoFormSettingsArray)[number]["formId"];
export const MarketoFormId = Object.freeze(_marketoFormSettingsArray.map((f) => f.formId));


type GetMktoFormSettings<TMktoKey extends MarketoFormSettingsKey> =
  MarketoFormSettingsObject[ TMktoKey ];

export type AllowedGatingStatesForMktoForm<TMktoKey extends MarketoFormSettingsKey> =
  GetMktoFormSettings<TMktoKey>[ "marketoAllowedGatingStates" ][ number ];

export interface MarketoFormSettings<
  TAllowedGatingState extends GatingState.MarketoForm = GatingState.MarketoForm
> {
  readonly marketoFormSettingsKey: MarketoFormSettingsKey;
  readonly formId: MarketoFormId;
  readonly hasRecaptcha: boolean;
  readonly gatingCookiePermissions: GatingCookiePermissions;
  readonly marketoAllowedGatingStates: readonly TAllowedGatingState[];
  readonly enabledFields: {
    [Key in TAllowedGatingState]: readonly AnyMarketoInputName[];
  };
}